import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { AlertTriangle, Brain, Shield, Eye, Heart, Scale } from 'lucide-react';
import SEO from '../components/SEO';

export default function DisclaimerPage() {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-[#E8ECF1]" data-testid="disclaimer-page">
      <SEO
        title={language === 'fr' ? "Avant-propos — Les limites de l'IA" : "Foreword — The Limits of AI"}
        description={language === 'fr'
          ? "Comprendre les limites et les risques de l'intelligence artificielle. L'IA est un outil puissant mais son usage exige vigilance, éthique et esprit critique."
          : "Understanding the limits and risks of artificial intelligence. AI is a powerful tool but its use requires vigilance, ethics and critical thinking."}
        path="/disclaimer"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        <div className="flex items-center gap-3 mb-8">
          <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-10 h-10 rounded-lg" />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Poppins' }} data-testid="disclaimer-title">
              {language === 'fr' ? "Avant-propos" : "Foreword"}
            </h1>
            <p className="text-xs text-slate-400 tracking-wider font-medium mt-0.5">
              {language === 'fr' ? "Les limites de l'Intelligence Artificielle" : "The Limits of Artificial Intelligence"}
            </p>
          </div>
        </div>

        {/* Intro */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8 mb-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins' }} data-testid="disclaimer-intro-title">
                {language === 'fr'
                  ? "Un outil puissant, encore à ses débuts"
                  : "A powerful tool, still in its early stages"}
              </h2>
              <p className="text-slate-600 leading-relaxed">
                {language === 'fr'
                  ? "L'intelligence artificielle n'en est qu'aux prémices de ses possibilités. C'est un outil d'une puissance considérable, mais qui comporte des limites dont il est essentiel d'avoir pleinement conscience avant de l'utiliser. Maîtriser l'IA, c'est aussi comprendre ce qu'elle ne peut pas — et ne doit pas — faire."
                  : "Artificial intelligence is only at the beginning of its possibilities. It is a tool of considerable power, but one that comes with limitations that are essential to fully understand before using it. Mastering AI also means understanding what it cannot — and should not — do."}
              </p>
            </div>
          </div>
        </div>

        {/* Dangers */}
        <div className="bg-white rounded-xl border border-red-200 p-6 md:p-8 mb-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center shrink-0">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins' }} data-testid="disclaimer-dangers-title">
                {language === 'fr'
                  ? "Les dangers d'une utilisation irresponsable"
                  : "The dangers of irresponsible use"}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                {language === 'fr'
                  ? "Les risques liés à une mauvaise utilisation de l'IA sont multiples et potentiellement très graves :"
                  : "The risks associated with misuse of AI are numerous and potentially very serious:"}
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Eye className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">
                      {language === 'fr' ? "Un instrument de contrôle" : "An instrument of control"}
                    </p>
                    <p className="text-slate-600 text-sm mt-1">
                      {language === 'fr'
                        ? "Mal encadrée, l'IA peut devenir un outil de surveillance et de contrôle de masse. Geoffrey Hinton, ancien chercheur chez Google et considéré comme l'un des pères fondateurs de l'IA, alerte publiquement sur les risques de perte de contrôle et de désinformation à grande échelle. Même Elon Musk appelle à une régulation stricte des données fournies aux systèmes d'intelligence artificielle."
                        : "Without proper oversight, AI can become a tool for mass surveillance and control. Geoffrey Hinton, former Google researcher and considered one of the founding fathers of AI, publicly warns about the risks of loss of control and large-scale disinformation. Even Elon Musk calls for strict regulation of data fed to artificial intelligence systems."}
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Scale className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">
                      {language === 'fr' ? "Des dérives civilisationnelles possibles" : "Potential civilizational risks"}
                    </p>
                    <p className="text-slate-600 text-sm mt-1">
                      {language === 'fr'
                        ? "Les risques de dérives spirituelles, éthiques ou civilisationnelles sont réels. Lorsque l'IA influence nos croyances, nos décisions et notre vision du monde sans que nous en ayons conscience, elle peut altérer les fondements mêmes de nos sociétés. La vigilance collective est indispensable."
                        : "The risks of spiritual, ethical or civilizational drift are real. When AI influences our beliefs, decisions and worldview without our awareness, it can alter the very foundations of our societies. Collective vigilance is essential."}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* L'humain au centre */}
        <div className="bg-white rounded-xl border border-teal-200 p-6 md:p-8 mb-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-teal-50 border border-teal-200 flex items-center justify-center shrink-0">
              <Heart className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins' }} data-testid="disclaimer-human-title">
                {language === 'fr'
                  ? "L'humain doit rester au centre"
                  : "Humans must remain at the center"}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                {language === 'fr'
                  ? "L'IA ne pourra jamais remplacer notre intuition, nos sens, notre libre arbitre, notre jugement, nos sentiments et notre empathie."
                  : "AI will never be able to replace our intuition, our senses, our free will, our judgment, our feelings and our empathy."}
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {(language === 'fr'
                  ? [
                    { icon: Brain, label: "Son intuition", desc: "Ce sixième sens qui guide nos décisions au-delà de la logique pure" },
                    { icon: Heart, label: "Son empathie", desc: "La capacité à ressentir et comprendre les émotions d'autrui" },
                    { icon: Eye, label: "Ses sens", desc: "L'expérience sensorielle du monde réel, irréductible aux algorithmes" },
                    { icon: Scale, label: "Son libre arbitre", desc: "Le pouvoir de choisir, de douter et de remettre en question" },
                  ]
                  : [
                    { icon: Brain, label: "Intuition", desc: "That sixth sense guiding decisions beyond pure logic" },
                    { icon: Heart, label: "Empathy", desc: "The ability to feel and understand others' emotions" },
                    { icon: Eye, label: "Senses", desc: "The sensory experience of the real world, irreducible to algorithms" },
                    { icon: Scale, label: "Free will", desc: "The power to choose, to doubt and to question" },
                  ]
                ).map((item, i) => (
                  <div key={i} className="flex items-start gap-3 bg-teal-50/50 rounded-lg p-3 border border-teal-100">
                    <item.icon className="w-5 h-5 text-teal-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{item.label}</p>
                      <p className="text-slate-500 text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Conclusion */}
        <div className="bg-slate-900 rounded-xl p-6 md:p-8 text-center shadow-sm">
          <p className="text-white text-lg font-semibold leading-relaxed" style={{ fontFamily: 'Poppins' }} data-testid="disclaimer-conclusion">
            {language === 'fr'
              ? "L'IA est un formidable levier de progrès. Mais c'est à nous, humains, de définir ses limites, de garder notre esprit critique et de veiller à ce qu'elle reste un outil — jamais un maître."
              : "AI is a tremendous lever for progress. But it is up to us, humans, to define its limits, keep our critical thinking and ensure it remains a tool — never a master."}
          </p>
        </div>

      </div>
    </div>
  );
}
